import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, ProgressBar } from '../../../components';

export const BacklinksSection = ({ id, data }) => {
  const [backlinksExpanded, setBacklinksExpanded] = useState(false);

  const defaultCriteria = {
    status: 'info',
    iconType: 'info',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
    isEmpty: true,
    message: 'Data is unavailable.',
    data: null,
  };

  const backlinksScore = data?.backlinksScore || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  return (
    <ResultsItem 
      id={id}
      title="Backlinks" 
      tooltip="Backlinks are one of the most influential SEO ranking factors. Your backlink score is an analysis of your link profile and looks at the quality and quantity of your backlinks. Earning more high-quality backlinks indicates to Google that your site is authoritative and trustworthy, and results in a higher backlink score."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="Backlinks Score"
        tooltip="Your backlinks score analyzes your backlink profile, looking at the quantity and quality of links pointing to your site."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={backlinksScore?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!backlinksScore?.isEmpty && (
              <>
                <p className="text-primary">{backlinksScore?.data?.label || ''}</p>
                <div className="score-row">
                  <ProgressBar percentage={Number(backlinksScore?.data?.percent || 0)} color="#207DE9" />
                  <span className="score-text">{backlinksScore?.data?.scoreText || ''}</span>
                </div>
              </>
            )}
            
            {backlinksScore?.isEmpty && (
              <p className="text-primary">
                {backlinksScore?.message || 'The backlinks score was not able to be calculated.'}
              </p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={backlinksExpanded}
            onClick={() => setBacklinksExpanded(!backlinksExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={backlinksExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Backlink Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Focus on quality versus quantity</li>
                <li>Create useful content, like tools and guides, related to your industry</li>
                <li>Avoid buying links and any unethical link building practices</li>
              </ul>
            </IconContent>
          }
          sideContent={
            <PriorityIndicatorGroup>
              <PriorityIndicator text="High Impact" level={3} />
              <PriorityIndicator text="Hard to Solve" level={3} />
            </PriorityIndicatorGroup>
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/seo/glossary/what-are-backlinks/" target="_blank" rel="noopener noreferrer">
                    What Are Backlinks? (And How to Earn Backlinks in 2020)
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/how-many-backlinks-do-i-need/" target="_blank" rel="noopener noreferrer">
                    How Many Backlinks Should Your Website Have?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/glossary/what-is-a-backlink-analysis/" target="_blank" rel="noopener noreferrer">
                    What Is a Backlink Analysis and Why Does It Matter?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
    </ResultsItem>
  );
};
